// bluaIQ — TASK-WEB-6: website payload contract v3 (types + validator), ported 1:1
// from the promulgated TAKEOFF reference implementation (work_takeoff_repo,
// takeoff_system/website_payload_contract_v3.py, commits 861bc2d + fix 46d27dc,
// both Codex PASS).
//
// v3 is an ADDITIVE amendment over v2: it adds EXACTLY ONE allowed section —
// plan_qa_report — so the auditor-declared Plan QA product capability can reach
// the read-only view. Everything in v2 stays authoritative and unchanged: a
// v2-valid payload without plan_qa_report is also v3-valid; a payload WITH
// plan_qa_report must additionally satisfy the rules below. The v2 validator
// module is imported, never modified.
//
// plan_qa_report rules (mirroring the Python reference):
// - headline bilingual {en, es};
// - issues: list of {issue, status} with status in PLAN_QA_STATUSES; optional
//   before/after travel as-is (dimension records, no money);
// - designations_applied: list of designation ids (strings);
// - the money-string guard applies INSIDE plan_qa_report too — Plan QA must
//   never smuggle amounts to the client view;
// - label rule: a payload carrying plan_qa_report MUST declare itself v3 via
//   contract_version (violation plan_qa.payload_not_declared_v3).

import {
  MONEY_STRING_RX,
  iterStrings,
  validatePayloadV2,
} from './websitePayloadContractV2'
import type {
  BilingualLabel,
  ValidationResult,
  WebsiteTakeoffPayloadV2,
} from './websitePayloadContractV2'

export const CONTRACT_VERSION_V3 = 'v2-website-safe-readonly-takeoff-payload-contract-v3'
export const BASED_ON = 'v2-website-safe-readonly-takeoff-payload-contract-v2'

export const PLAN_QA_SECTION = 'plan_qa_report'

export const PLAN_QA_STATUSES = [
  'resolved_by_plan_revision',
  'resolved_by_user_designation',
  'label_absent_in_rev2',
  'label_error_confirmed_resolution_pending_corrected_dimension',
  'pending',
] as const

export type PlanQaIssueStatus = (typeof PLAN_QA_STATUSES)[number]

export interface PlanQaIssue {
  issue: string
  status: PlanQaIssueStatus
  /** Optional before/after travel AS-IS per the contract — the real generator ships
   *  either plain strings or structured records (e.g. dimension records
   *  {width_ft, depth_ft, area_sf, was_suspect}). Typed as unknown so the UI is
   *  forced through a safe formatter and can never interpolate an object directly
   *  (TASK-WEB-7 Codex blocker: string typing let "[object Object]" reach render). */
  before?: unknown
  after?: unknown
}

export interface PlanQaReport {
  headline: BilingualLabel
  issues: PlanQaIssue[]
  designations_applied: string[]
}

/** v3 payload: everything v2 plus the optional plan_qa_report section and the
 *  contract_version label that MUST be present when plan_qa_report is. */
export interface WebsiteTakeoffPayloadV3 extends WebsiteTakeoffPayloadV2 {
  contract_version?: string
  plan_qa_report?: PlanQaReport
  /** ADDITIVE, OPTIONAL carrier for the insulation lane's client_questions
   *  (sealed contract `insulation-lane-checkpoint-v1`). The validator is already
   *  additive — it does NOT reject this unknown top-level key, and its recursive
   *  FORBIDDEN_FIELDS walk still rejects any raw_text nested inside — so no
   *  validator change is needed (verified: a real checkpoint payload carrying
   *  this block passes validatePayloadV3 cleanly).
   *
   *  `client_questions` is typed `unknown[]` on PURPOSE, exactly like
   *  PlanQaIssue.before: every consumer MUST pass each element through
   *  `assertClientQuestion` (takeoffClientQuestion.ts), whose exactly-4-key
   *  evidence allowlist and raw_text rejection are the real safety boundary —
   *  never this type. Typing it as the finished shape would invite a consumer to
   *  render it un-validated. */
  insulation_lane?: {
    client_questions?: unknown[]
  }
}

/**
 * v2 rules first (unchanged), then plan_qa_report rules when present.
 * Mirrors validate_payload_v3() in the promulgated Python reference.
 */
export function validatePayloadV3(payload: unknown): ValidationResult {
  const base = validatePayloadV2(payload)
  const violations = [...base.violations]

  const p = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : null
  const report = p ? p[PLAN_QA_SECTION] : undefined
  if (report !== undefined && report !== null) {
    // Contractual clarity (auditor rule): content and label must match — a
    // payload carrying plan_qa_report MUST declare itself v3.
    if (p!.contract_version !== CONTRACT_VERSION_V3) {
      violations.push('plan_qa.payload_not_declared_v3')
    }
    const r = report as Record<string, unknown>
    const headline = r.headline as { en?: unknown; es?: unknown } | undefined
    if (!(headline && typeof headline === 'object' && headline.en && headline.es)) {
      violations.push('plan_qa.headline_not_bilingual')
    }
    const issues = r.issues
    if (!Array.isArray(issues) || issues.length === 0) {
      violations.push('plan_qa.issues_missing_or_empty')
    } else {
      (issues as Record<string, unknown>[]).forEach((issue, i) => {
        if (!issue.issue) {
          violations.push(`plan_qa.issues[${i}].missing_issue`)
        }
        if (!PLAN_QA_STATUSES.includes(issue.status as PlanQaIssueStatus)) {
          violations.push(`plan_qa.issues[${i}].invalid_status:${String(issue.status)}`)
        }
      })
    }
    if (!Array.isArray(r.designations_applied)) {
      violations.push('plan_qa.designations_applied_not_a_list')
    }
    for (const text of iterStrings(r)) {
      const match = MONEY_STRING_RX.exec(text)
      if (match) {
        violations.push(`plan_qa.money_string_present:${match[0]}`)
        break
      }
    }
  }

  return { valid: violations.length === 0, violations, contract_version: CONTRACT_VERSION_V3 }
}
